import { LoadingButton } from "@mui/lab";
import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography
} from "@mui/material";
import React, { Dispatch, SetStateAction, useContext, useRef, useState } from "react";

import { UserContext } from "../../../../Context/UserContext";
import { EnhancedPhoneNumber, PhoneNumberType } from "../../../../Models/PhoneNumber";
import { DateTime } from "../../../../Types/DateTime";
import { enumAsKVArray, phoneIsValid } from "../../../../Utils/Helpers";
import { PhoneFieldMasked } from "../../../Helpers/PhoneNumberField";
import { ValidatedTextField } from "../../../Helpers/ValidatedTextField";
import AdminReadmeFormLabel from "../../AdminReadmeFormLabel";
import { PhoneNumberSettingsReadme } from "./PhoneNumberSettings.Readme";

const phoneNumberTypes = enumAsKVArray(PhoneNumberType).sort((a, b) =>
  (a.value as string) > (b.value as string) ? 1 : -1
);

const formatPhoneNumber = (value: string) =>
  `(${value.substring(0, 3)}) ${value.substring(3, 6)}-${value.substring(6)}`;

interface PhoneNumberSettingsEditFormProps {
  isLoading: boolean;
  phoneNumber?: EnhancedPhoneNumber;
  phoneNumbers: EnhancedPhoneNumber[];
  setPhoneNumber: Dispatch<SetStateAction<EnhancedPhoneNumber | undefined>>;
  setUpdate: Dispatch<EnhancedPhoneNumber>;
}

const PhoneNumberSettingsEditForm: React.FC<PhoneNumberSettingsEditFormProps> = ({
  isLoading,
  phoneNumber,
  phoneNumbers,
  setPhoneNumber,
  setUpdate
}) => {
  const {
    user: { username }
  } = useContext(UserContext);
  const [conflictingPhoneNumber, setConflictingPhoneNumber] = useState<EnhancedPhoneNumber>();
  const numberFieldRef = useRef<HTMLDivElement | null>(null);
  const onNewNumber = () => {
    numberFieldRef.current?.focus();
    setPhoneNumber(
      new EnhancedPhoneNumber({
        note: undefined,
        value: "",
        type: PhoneNumberType["No Action"],
        createdByUsername: username
      })
    );
  };
  const originalNumber = phoneNumber?.id && phoneNumbers.find(p => p.id === phoneNumber.id);
  const isChange =
    !originalNumber ||
    JSON.stringify(EnhancedPhoneNumber.toBase(originalNumber)) !==
      JSON.stringify(EnhancedPhoneNumber.toBase(phoneNumber));
  const onPhoneChange = (value: string) => {
    setPhoneNumber(p => p && { ...p, value });
    setConflictingPhoneNumber(
      EnhancedPhoneNumber.isValid(value) ? phoneNumbers.find(p => p.value === value) : undefined
    );
  };

  return (
    <Stack spacing={2} sx={{ minWidth: "18rem" }}>
      <Typography variant="h6">
        {phoneNumber
          ? phoneNumber?.id
            ? `Editing ${formatPhoneNumber(phoneNumber.value)}`
            : "New Number"
          : "Nothing Selected"}
      </Typography>
      <FormControl variant="outlined" margin="dense" size="small" fullWidth sx={{ m: 0 }}>
        <InputLabel htmlFor="formatted-text-mask-input">Phone</InputLabel>
        <OutlinedInput
          margin="dense"
          size="small"
          value={phoneNumber?.value || ""}
          label="Phone"
          error={Boolean(conflictingPhoneNumber || (phoneNumber?.value && phoneIsValid(phoneNumber.value)))}
          disabled={!phoneNumber || Boolean(phoneNumber.id)}
          onChange={e => onPhoneChange(e.target.value.replace(/[^0-9]/g, ""))}
          inputComponent={PhoneFieldMasked as never}
          ref={(ref: HTMLDivElement) => (numberFieldRef.current = ref)}
        />
        <FormHelperText error>{conflictingPhoneNumber ? "Phone number already exists." : " "}</FormHelperText>
      </FormControl>
      <Stack spacing={1}>
        <AdminReadmeFormLabel label="Type" readme={PhoneNumberSettingsReadme.type} />
        <Select
          value={phoneNumber?.type || ""}
          onChange={e => setPhoneNumber(p => (p ? { ...p, type: e.target.value as PhoneNumberType } : p))}
          fullWidth
          disabled={!phoneNumber}
        >
          {phoneNumberTypes.map(({ key, value }, index) => (
            // TODO: remove disabling of blocked typed
            <MenuItem value={value as number} key={index} disabled={value === PhoneNumberType["Blocked"]}>
              {key}
            </MenuItem>
          ))}
        </Select>
      </Stack>
      <ValidatedTextField
        label="Note"
        multiline
        minRows={6}
        maxRows={12}
        fullWidth
        value={phoneNumber?.note || ""}
        onChange={value => setPhoneNumber(p => (p ? { ...p, note: value } : p))}
        onAccepted={() => null}
        tests={[{ test: value => !phoneNumber || Boolean(value), message: "Must provide a note." }]}
        disabled={!phoneNumber}
      />
      <Stack spacing={1}>
        <Typography variant="h6" component="span">
          Created:
        </Typography>
        <Typography variant="body1" component="span">{` ${phoneNumber?.createdByUsername || "System"}${
          phoneNumber?.createdAt ? ` - ${phoneNumber.createdAt.toLocaleString(DateTime.DATETIME_MED)}` : ""
        }`}</Typography>
      </Stack>
      <Stack>
        <Typography variant="h6" component="span">
          Updated:
        </Typography>
        <Typography variant="body1" component="span">{` ${phoneNumber?.updatedByUsername || "No Updates"}${
          phoneNumber?.updatedAt ? ` - ${phoneNumber.updatedAt.toLocaleString(DateTime.DATETIME_MED)}` : ""
        }`}</Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-around" spacing={2}>
        <Button disabled={Boolean(phoneNumber && !phoneNumber.id) || isLoading} onClick={onNewNumber}>
          Add New
        </Button>
        <LoadingButton
          variant="outlined"
          loading={isLoading}
          disabled={phoneNumber?.value?.length !== 10 || !isChange || isLoading || !phoneNumber?.note}
          onClick={() => phoneNumber && setUpdate(phoneNumber)}
        >
          Save Changes
        </LoadingButton>
      </Stack>
    </Stack>
  );
};

export default PhoneNumberSettingsEditForm;
